import React from "react";
import "./App.css";
import { Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { ScrollToTop } from "./components/common";
import { trackArrival } from "./lib/shareTrack";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { publicApi, mediaUrl } from "./lib/api";
import { organizationSchema, websiteSchema } from "./components/Seo";
import { initConversionTracking } from "./lib/analytics";
import { pushToDataLayer } from "./lib/dataLayer";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import DiscoveryPopup from "./components/DiscoveryPopup";
import ChatWidget from "./components/ChatWidget";
import MobileStickyCta from "./components/MobileStickyCta";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Podcast from "./pages/Podcast";
import Testimonials from "./pages/Testimonials";
import Contact from "./pages/Contact";
import Team from "./pages/Team";
import Careers from "./pages/Careers";
import DynamicPage from "./pages/DynamicPage";
import PodcastSeriesDetail from "./pages/PodcastSeriesDetail";
import AllEpisodes from "./pages/AllEpisodes";
import BuildPackage from "./pages/BuildPackage";
import MeetingRoom from "./pages/MeetingRoom";
import Reviews from "./pages/Reviews";
import Faqs from "./pages/Faqs";
import AskCassie from "./pages/AskCassie";
import AskCassieSlug from "./pages/AskCassieSlug";
import AskCassieReviewer from "./pages/AskCassieReviewer";
import Insights from "./pages/Insights";
import SuccessStory from "./pages/SuccessStory";
import BusinessHealthCheck from "./pages/BusinessHealthCheck";
import BusinessAssessmentTools from "./pages/BusinessAssessmentTools";
import BusinessNumbersCalculator from "./pages/BusinessNumbersCalculator";
import AssessmentRunner from "./components/AssessmentRunner";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import CookieConsent from "./components/CookieConsent";
import LegalCashiering from "./pages/LegalCashiering";
import LegalCashieringService from "./pages/LegalCashieringService";
import LegalCashieringCaseStudy from "./pages/LegalCashieringCaseStudy";
import LocationPage from "./pages/LocationPage";
import XeroLocationPage from "./pages/XeroLocationPage";
import XeroAccountantsHub from "./pages/XeroAccountantsHub";
import { XERO_LOCATIONS } from "./data/xeroLocations";
import ServiceLocationPage from "./pages/ServiceLocationPage";
import { SERVICE_LOCATIONS } from "./data/serviceLocations";
import AreasWeSupport from "./pages/AreasWeSupport";
import MtdChecker from "./pages/MtdChecker";
import KeyTaxDates from "./pages/KeyTaxDates";
import SwitchingAccountants from "./pages/SwitchingAccountants";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminApp from "./pages/admin/AdminApp";
import PagePreview from "./pages/admin/PagePreview";
import LegacyRedirect from "./components/LegacyRedirect";
import LegalCashieringAdmin from "./pages/admin/LegalCashieringAdmin";

// Site-wide structured data (Organization + WebSite) injected on every public page
function GlobalSeo() {
  const [founder, setFounder] = React.useState(null);
  React.useEffect(() => {
    publicApi.homepage().then((hp) => {
      const f = hp && hp.founder;
      if (!f) return;
      const bio = (f.bio || "").replace(/\s+/g, " ").trim();
      const hasPlaceholder = bio.includes("[");
      setFounder({
        name: f.name,
        role: f.role,
        imageUrl: f.image ? mediaUrl(f.image) : "",
        description: !hasPlaceholder && bio ? bio.slice(0, 320) : "",
        sameAs: f.linkedin ? [f.linkedin] : [],
      });
    }).catch(() => {});
  }, []);
  return (
    <Helmet>
      {/* Schema only. Page title / description / canonical / OG are emitted solely
          by the page-level <Seo> component (src/components/Seo.jsx), which carries
          its own site-wide fallbacks. Duplicating them here produced two <title>
          and two meta description tags per page — see SEO fix A1-1. */}
      <script type="application/ld+json">{JSON.stringify(organizationSchema(founder))}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
    </Helmet>
  );
}

// Public site layout (with header/footer)
function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-clip pb-14 lg:pb-0">
      <GlobalSeo />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <DiscoveryPopup />
      <ChatWidget />
      <MobileStickyCta />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

// Counts arrivals from shared links, and whether they went anywhere afterwards.
function ShareTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const path = location.pathname.length > 1 ? location.pathname.replace(/\/+$/, "") : location.pathname;
    trackArrival(path);
  }, [location.pathname]);
  return null;
}

// Tells Tag Manager about each client-side navigation, so the GA4 tag in the
// container records one page view per page — the browser fires no automatic one
// (single-page app), so there is nothing to duplicate.
function GaTracker() {
  const location = useLocation();
  const lastPath = React.useRef("");
  React.useEffect(() => { initConversionTracking(); }, []);
  React.useEffect(() => {
    const path = location.pathname + location.search;
    // One push per page actually shown. Without this guard a remount — React's
    // development double-render, or a component re-created — counts the same
    // page view twice.
    if (lastPath.current === path) return;
    lastPath.current = path;
    pushToDataLayer("page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      page_referrer: document.referrer || undefined,
    });
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <GaTracker />
          <ShareTracker />
          <LegacyRedirect>
          <Routes>
            {/* Public site */}
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/podcast/all" element={<AllEpisodes />} />
              <Route path="/podcast/series/:slug" element={<PodcastSeriesDetail />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/success-stories/:slug" element={<SuccessStory />} />
              {/* One pricing destination: the guided journey is served at /pricing,
                  the original and stronger URL. /build-your-package redirects here
                  (lib/legacyRedirects.js + public/_redirects). The journey replaced
                  the original package builder on 17 Jun 2026 after review at
                  /pricing-preview, which now redirects here. */}
              <Route path="/pricing" element={<BuildPackage />} />
              <Route path="/pricing-preview" element={<Navigate to="/pricing" replace />} />
              <Route path="/meeting-room-hire" element={<MeetingRoom />} />
              <Route path="/request-a-quote" element={<Navigate to="/contact" replace />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/faqs" element={<Faqs />} />
              {/* Ask Cassie — the Knowledge Centre. Detailed answers sit directly
                  under /ask-cassie/ so recategorising can never break a link. */}
              <Route path="/ask-cassie" element={<AskCassie />} />
              <Route path="/ask-cassie/reviewer/:slug" element={<AskCassieReviewer />} />
              <Route path="/ask-cassie/:slug" element={<AskCassieSlug />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/business-health-check" element={<Navigate to="/more-than-numbers/business-health-score" replace />} />
              <Route path="/more-than-numbers/business-assessment-tools" element={<BusinessAssessmentTools />} />
              <Route path="/business-numbers-calculator" element={<BusinessNumbersCalculator />} />
              <Route path="/making-tax-digital-income-tax" element={<MtdChecker />} />
              <Route path="/key-tax-dates" element={<KeyTaxDates />} />
              <Route path="/switching-accountants" element={<SwitchingAccountants />} />
              <Route path="/more-than-numbers/business-health-score" element={<AssessmentRunner slug="business-health-score" />} />
              <Route path="/more-than-numbers/owner-independence-score" element={<AssessmentRunner slug="owner-independence-score" />} />
              <Route path="/more-than-numbers/growth-readiness-score" element={<AssessmentRunner slug="growth-readiness-score" />} />
              <Route path="/more-than-numbers/financial-awareness-score" element={<AssessmentRunner slug="financial-awareness-score" />} />
              <Route path="/more-than-numbers/10-numbers-every-business-owner-should-know" element={<Navigate to="/more-than-numbers/financial-awareness-score" replace />} />
              <Route path="/10-numbers-every-business-owner-should-know" element={<Navigate to="/more-than-numbers/financial-awareness-score" replace />} />
              <Route path="/legal-cashiering" element={<LegalCashiering />} />
              <Route path="/legal-cashiering/case-study/:slug" element={<LegalCashieringCaseStudy />} />
              <Route path="/legal-cashiering/:slug" element={<LegalCashieringService />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/p/:slug" element={<DynamicPage />} />
              <Route path="/areas-we-support" element={<AreasWeSupport />} />
              <Route path="/xero-accountants" element={<XeroAccountantsHub />} />
              {XERO_LOCATIONS.map((x) => (
                <Route key={x.slug} path={`/${x.slug}`} element={<XeroLocationPage slug={x.slug} />} />
              ))}
              {SERVICE_LOCATIONS.map((s) => (
                <Route key={s.slug} path={`/${s.slug}`} element={<ServiceLocationPage slug={s.slug} />} />
              ))}
              <Route path="/:locationSlug" element={<LocationPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin" element={<RequireAuth><AdminApp /></RequireAuth>} />
            <Route path="/admin/pages/:id/preview" element={<RequireAuth><PagePreview /></RequireAuth>} />
            <Route path="/admin/legal-cashiering" element={<RequireAuth><LegalCashieringAdmin /></RequireAuth>} />
          </Routes>
          </LegacyRedirect>
          <CookieConsent />
          <Toaster />
          <SonnerToaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
